
import { useEffect, useMemo, useState } from 'react';
import { AudioLines, CircleAlert, Loader2, RefreshCw, Star, TriangleAlert } from 'lucide-react';

import type { VoiceCatalogParams } from '../../api/endpoints';
import { ErrorBanner } from '../../components/ErrorBanner';
import { TeamSelect } from '../../components/TeamSelect';
import { teamLabel } from '../../lib/team';
import {
  Button,
  Card,
  EmptyState,
  Pill,
  PlayButton,
  Scrubber,
  SearchInput,
  Seg,
  Select,
  SkeletonRows,
  Table,
  TableActions,
  tableCell,
  useAudioPreview,
  type AudioPreview,
  type PillTone,
  type SegOption,
} from '../../components/ui';
import {
  taskProgressPct,
  useCloneVoice,
  useCurrentUser,
  useUpdateVoice,
  useVoiceSample,
  useVoices,
} from '../../hooks';
import type { Id, TtsProvider, Voice, VoiceCatalogEntry, VoiceStatus } from '../../types/api';
import { formatDateTime, languageLabel, plural } from '../tts/format';
import type { VoiceGroup } from '../tts/voiceOptions';
import { useRefreshVoiceCatalog, useVoiceCatalog, useVoiceCatalogStats } from './catalogQueries';
import { CreateVoiceForm } from './CreateVoiceForm';
import {
  accentLabel,
  ageLabel,
  genderLabel,
  traitLine,
  purposeLabel,
  VOICE_AGES,
  VOICE_GENDERS,
} from './vocab';
import styles from './VoicesPage.module.css';

const PAGE_LIMIT = 200;

const CATALOG_BASE: VoiceCatalogParams = { available: true, limit: PAGE_LIMIT };

// The catalog sync owns "the clone is gone" and records it in the profile status,
// so the tab never has to compare profiles against a page of catalog ids.
const BROKEN_STATUSES: VoiceStatus[] = ['evicted', 'failed'];
const LINKED_STATUSES: VoiceStatus[] = ['ready', 'pending'];

type CategoryKey = 'cloned' | 'professional' | 'premade';

type FilterKey = 'all' | CategoryKey | 'broken';

const CATEGORIES: readonly { key: CategoryKey; label: string }[] = [
  { key: 'cloned', label: 'Клонированные' },
  { key: 'professional', label: 'Профессиональные' },
  { key: 'premade', label: 'Стандартные' },
];

interface RowState {
  label: string;
  tone: PillTone;
  broken: boolean;
  hint: string | null;
}

const STATE_READY: RowState = { label: 'готов', tone: 'ok', broken: false, hint: null };

function stateOf(voice: Voice): RowState {
  if (voice.status === 'evicted') {
    return {
      label: 'вытеснен',
      tone: 'danger',
      broken: true,
      hint: 'Голос больше не работает. Запись сохранилась, создайте его заново.',
    };
  }
  if (voice.status === 'failed') {
    return {
      label: 'ошибка клона',
      tone: 'danger',
      broken: true,
      hint: 'Клонирование не удалось. Попробуйте ещё раз, образец на месте.',
    };
  }
  if (voice.status === 'pending') {
    return voice.external_voice_id
      ? { label: 'клонируется', tone: 'warn', broken: false, hint: null }
      : {
          label: 'без клона',
          tone: 'warn',
          broken: false,
          hint: 'Запись загружена, голос ещё не создан.',
        };
  }
  if (voice.usable) return STATE_READY;
  return {
    label: 'недоступен',
    tone: 'danger',
    broken: true,
    hint: 'Бэк не считает голос пригодным для синтеза.',
  };
}

function describeTimbre(entry: VoiceCatalogEntry): string {
  return traitLine([
    accentLabel(entry.accent),
    genderLabel(entry.gender),
    ageLabel(entry.age),
    purposeLabel(entry.use_case),
  ]);
}

function groupOf(category: string | null): VoiceGroup {
  if (category === 'professional') return 'pro';
  if (category === 'premade' || category === 'generated') return 'std';
  return 'mine';
}

function profileRow(voice: Voice): VoiceRow {
  return {
    key: `profile:${voice.id}`,
    entry: null,
    voice,
    name: voice.name,
    group: 'mine',
    language: voice.language || null,
    timbre: '',
    description: '',
    characterName: voice.character_name,
    previewUrl: null,
    state: stateOf(voice),
  };
}

const GROUP_LABELS: Readonly<Record<VoiceGroup, string>> = {
  mine: 'клон',
  pro: 'проф',
  std: 'станд',
};

interface VoiceRow {
  key: string;
  entry: VoiceCatalogEntry | null;
  voice: Voice | null;
  name: string;
  group: VoiceGroup;
  language: string | null;
  timbre: string;
  description: string;
  characterName: string | null;
  previewUrl: string | null;
  state: RowState;
}

export function AccountVoicesTab({ provider }: { provider: TtsProvider }) {
  const { can } = useCurrentUser();
  const canEdit = can('editor');

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const audio = useAudioPreview();
  const clone = useCloneVoice();

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const category = filter === 'all' || filter === 'broken' ? undefined : filter;
  const byTrait = Boolean(gender) || Boolean(age);
  const narrowed = Boolean(debounced) || Boolean(category) || byTrait;

  const catalogBase = useMemo(() => ({ ...CATALOG_BASE, provider }), [provider]);
  const base = useVoiceCatalog(catalogBase);
  const shown = useVoiceCatalog(
    narrowed
      ? {
          ...catalogBase,
          category,
          q: debounced || undefined,
          gender: gender || undefined,
          age: age || undefined,
        }
      : catalogBase,
  );
  const stats = useVoiceCatalogStats(provider);
  const refresh = useRefreshVoiceCatalog(stats.data?.last_synced_at);
  const linked = useVoices({ limit: PAGE_LIMIT, provider, status: LINKED_STATUSES });
  const pendingProfiles = useVoices({ limit: PAGE_LIMIT, provider, status: 'pending' });
  const brokenCount = useVoices({ limit: 1, provider, status: BROKEN_STATUSES });
  const brokenList = useVoices({
    limit: PAGE_LIMIT,
    provider,
    status: BROKEN_STATUSES,
    q: debounced || undefined,
  });

  const catalogKnown = base.isSuccess && base.data.items.length > 0;

  const ours = useMemo(() => {
    const byExternal = new Map<string, Voice>();
    const byProfile = new Map<Id, Voice>();
    for (const voice of linked.data?.items ?? []) {
      if (voice.external_voice_id) byExternal.set(voice.external_voice_id, voice);
      byProfile.set(voice.id, voice);
    }
    return { byExternal, byProfile };
  }, [linked.data]);

  // A pending profile with an id still in the catalog is already shown on its catalog row.
  const orphans = useMemo(() => {
    const catalogIds = new Set((base.data?.items ?? []).map((entry) => entry.external_voice_id));
    return (pendingProfiles.data?.items ?? []).filter(
      (voice) => !voice.external_voice_id || !catalogIds.has(voice.external_voice_id),
    );
  }, [pendingProfiles.data, base.data]);

  const counts = useMemo(() => {
    const byCategory = stats.data?.by_category ?? {};
    return {
      cloned: (byCategory.cloned ?? 0) + orphans.length,
      professional: byCategory.professional ?? 0,
      premade: byCategory.premade ?? 0,
    } satisfies Record<CategoryKey, number>;
  }, [stats.data, orphans]);

  const visible = useMemo<VoiceRow[]>(() => {
    // пол и возраст есть только в каталоге провайдера: у профиля таких полей нет,
    // и под фильтром по ним профили пропадают, а не «проходят как неизвестные»
    if (filter === 'broken') {
      return byTrait ? [] : (brokenList.data?.items ?? []).map(profileRow);
    }

    const needle = debounced.toLowerCase();
    const orphanRows = (byTrait ? [] : orphans)
      .filter((voice) => {
        if (category && category !== 'cloned') return false;
        return !needle || voice.name.toLowerCase().includes(needle);
      })
      .map(profileRow);

    const catalogRows = (shown.data?.items ?? []).map<VoiceRow>((entry) => {
      const voice =
        ours.byExternal.get(entry.external_voice_id) ??
        (entry.voice_profile_id ? (ours.byProfile.get(entry.voice_profile_id) ?? null) : null);
      return {
        key: `catalog:${entry.id}`,
        entry,
        voice,
        name: voice?.name || entry.name,
        group: groupOf(entry.category),
        language: entry.language || voice?.language || null,
        timbre: describeTimbre(entry),
        description: entry.description,
        characterName: voice?.character_name ?? null,
        previewUrl: entry.preview_url,
        state: voice ? stateOf(voice) : STATE_READY,
      };
    });

    return [...orphanRows, ...catalogRows];
  }, [filter, brokenList.data, orphans, shown.data, ours, category, debounced, byTrait]);

  const broken = brokenCount.data?.total ?? 0;

  const cloneBusy = clone.clone.isPending || clone.task.isRunning;
  const cloningId = cloneBusy ? clone.clone.variables : undefined;
  const cloneTask = clone.task.task.data;
  const clonePercent = taskProgressPct(cloneTask);

  const startClone = (voiceId: Id) => {
    audio.stop();
    clone.clone.mutate(voiceId);
  };

  const total = stats.data?.available ?? 0;
  const listed = filter === 'broken' ? brokenList : shown;
  const shownTotal = listed.data?.total ?? 0;
  const shownCount = listed.data?.items.length ?? 0;
  const pending =
    filter === 'broken' ? brokenList.isPending : shown.isPending || linked.isPending;
  const profilesError = linked.error ?? pendingProfiles.error ?? brokenList.error;
  const retryProfiles = () => {
    void linked.refetch();
    void pendingProfiles.refetch();
    void brokenList.refetch();
  };

  const filterOptions: SegOption<FilterKey>[] = [
    { value: 'all', label: `Все · ${total + orphans.length}` },
    ...CATEGORIES.map((item) => ({ value: item.key, label: `${item.label} · ${counts[item.key]}` })),
    ...(broken > 0 ? [{ value: 'broken' as const, label: `Сломанные · ${broken}` }] : []),
  ];

  return (
    <>
      <div className={styles.toolbar}>
        <SearchInput
          className={styles.search}
          value={query}
          onChange={setQuery}
          placeholder="Поиск по имени голоса"
          ariaLabel="Поиск по имени голоса"
        />

        <Seg value={filter} onChange={setFilter} options={filterOptions} ariaLabel="Тип голоса" />

        <Select
          className={styles.filterSelect}
          size="sm"
          value={gender}
          onChange={(event) => setGender(event.target.value)}
          aria-label="Пол голоса"
        >
          <option value="">Любой пол</option>
          {VOICE_GENDERS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>

        <Select
          className={styles.filterSelect}
          size="sm"
          value={age}
          onChange={(event) => setAge(event.target.value)}
          aria-label="Возраст голоса"
        >
          <option value="">Любой возраст</option>
          {VOICE_AGES.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>

        <span className={styles.toolbarActions}>
          <span className={styles.panelNote}>
            синхронизация: {formatDateTime(stats.data?.last_synced_at)}
          </span>
          <Button
            size="sm"
            variant="ghost"
            iconOnly
            onClick={() => refresh.start.mutate(CATALOG_BASE)}
            disabled={refresh.start.isPending || refresh.cooling}
            title={
              refresh.cooling
                ? 'Только что обновляли, следующий раз через минуту'
                : 'Обновить список голосов'
            }
            aria-label="Обновить список голосов"
          >
            <RefreshCw
              size={13}
              strokeWidth={1.6}
              className={refresh.start.isPending ? styles.spin : undefined}
            />
          </Button>
          {canEdit && (
            <Button onClick={() => setShowCreate((value) => !value)}>Новый голос</Button>
          )}
        </span>
      </div>

      {refresh.start.error && <ErrorBanner error={refresh.start.error} />}

      {!canEdit && (
        <div className={styles.notice}>
          <CircleAlert size={14} strokeWidth={1.5} className={styles.noticeIcon} />
          <span>
            Роль «наблюдатель»: смотреть и слушать можно, менять голоса нельзя.
          </span>
        </div>
      )}

      {showCreate && canEdit && (
        <CreateVoiceForm
          onClose={() => setShowCreate(false)}
          onClone={startClone}
          cloneBusy={cloneBusy}
          provider={provider}
        />
      )}

      {broken > 0 && filter !== 'broken' && (
        <div className={styles.alarm}>
          <TriangleAlert size={15} strokeWidth={1.5} className={styles.alarmIcon} />
          <span className={styles.alarmText}>
            {broken} {plural(broken, 'голос', 'голоса', 'голосов')} больше не работают. Старые
            озвучки открываются, новые ими не сделать. Записи сохранились, голоса можно создать
            заново.
          </span>
          <Button size="sm" onClick={() => setFilter('broken')}>
            Показать
          </Button>
        </div>
      )}

      {cloneBusy && (
        <div className={styles.running} role="status" aria-live="polite">
          <span className={styles.dot} />
          <span className={styles.runningTitle}>Создаём голос</span>
          <span className={styles.mono}>
            {clonePercent !== null ? `${clonePercent}%` : 'идёт'}
          </span>
          <span className={styles.panelNote}>
            дождитесь конца
          </span>
          <span className={styles.grow} />
          <Loader2 size={14} strokeWidth={2} />
        </div>
      )}
      {clone.clone.error && <ErrorBanner error={clone.clone.error} />}
      {cloneTask?.status === 'failed' && (
        <div className={styles.alarm}>
          <CircleAlert size={15} strokeWidth={1.5} className={styles.alarmIcon} />
          <span className={styles.alarmText}>
            Не получилось создать голос: {cloneTask.error ?? 'причина неизвестна'}. Попробуйте
            ещё раз.
          </span>
        </div>
      )}

      {(base.error || (base.isSuccess && !catalogKnown)) && (
        <div className={styles.notice}>
          <CircleAlert size={14} strokeWidth={1.5} className={styles.noticeIcon} />
          <span>
            {base.error
              ? 'Список голосов не загрузился, показаны только заведённые вами.'
              : 'Голосов пока нет.'}
          </span>
        </div>
      )}

      {pending && (
        <Card>
          <div className={styles.pad}>
            <SkeletonRows rows={6} />
          </div>
        </Card>
      )}

      {profilesError && <ErrorBanner error={profilesError} onRetry={retryProfiles} />}

      {!pending && visible.length === 0 && (
        <Card>
          <EmptyState
            icon={<AudioLines size={18} strokeWidth={1.5} />}
            title={
              filter === 'broken'
                ? 'Сломанных голосов нет'
                : narrowed
                  ? 'Ничего не нашлось'
                  : 'В аккаунте нет ни одного голоса'
            }
            description={
              filter === 'broken'
                ? 'Все голоса на месте, фильтр можно снять.'
                : narrowed
                  ? 'Поиск идёт по названию голоса, имя персонажа в него не входит. Снимите фильтры или очистите поиск.'
                  : canEdit
                    ? 'Голосов пока нет. Заведите свой из записи или возьмите готовый во вкладке «Библиотека ElevenLabs».'
                    : 'Голосов пока нет.'
            }
            action={
              narrowed || filter !== 'all' ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setQuery('');
                    setFilter('all');
                    setGender('');
                    setAge('');
                  }}
                >
                  Показать все
                </Button>
              ) : undefined
            }
          />
        </Card>
      )}

      {visible.length > 0 && (
        <Card>
          <Table bare className={styles.voiceTable}>
            <thead>
              <tr>
                <th className={styles.colPlay} />
                <th className={styles.ourName}>голос</th>
                <th className={styles.colKind}>тип</th>
                <th className={styles.colLang}>язык</th>
                <th>тембр</th>
                <th className={styles.ourCharacter}>персонаж</th>
                <th>автор</th>
                <th className={styles.colState}>состояние</th>
                <th className={styles.ourActions} />
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <VoiceTableRow
                  key={row.key}
                  row={row}
                  audio={audio}
                  canEdit={canEdit}
                  cloning={Boolean(row.voice) && cloningId === row.voice?.id}
                  cloneBlocked={cloneBusy}
                  onClone={startClone}
                />
              ))}
            </tbody>
          </Table>

          {shownTotal > shownCount && (
            <p className={styles.footNote}>
              Показаны {visible.length} из {shownTotal}: сервер отдаёт не больше {PAGE_LIMIT}{' '}
              голосов за запрос. Уточните поиск по имени.
            </p>
          )}
        </Card>
      )}
    </>
  );
}

function VoiceTableRow({
  row,
  audio,
  canEdit,
  cloning,
  cloneBlocked,
  onClone,
}: {
  row: VoiceRow;
  audio: AudioPreview;
  canEdit: boolean;
  cloning: boolean;
  cloneBlocked: boolean;
  onClone: (voiceId: Id) => void;
}) {
  const { voice, previewUrl } = row;
  const [sampleRequested, setSampleRequested] = useState(false);
  const sample = useVoiceSample(voice?.id, sampleRequested && !previewUrl);

  const playing = audio.playingKey === row.key;
  const { toggle, stop } = audio;
  const sampleUrl = previewUrl ?? sample.data?.url;
  const language = row.language ? languageLabel(row.language) : 'нет';
  const canPlay = Boolean(previewUrl) || Boolean(voice);

  useEffect(() => {
    if (!sampleRequested || !sampleUrl) return;
    setSampleRequested(false);
    toggle(row.key, sampleUrl);
  }, [sampleRequested, sampleUrl, toggle, row.key]);

  const handlePlay = () => {
    if (playing) {
      stop();
      return;
    }
    if (sampleUrl) {
      toggle(row.key, sampleUrl);
      return;
    }
    setSampleRequested(true);
  };

  return (
    <tr className={row.state.broken ? styles.rowBroken : undefined}>
      <td>
        <div className={styles.playCell}>
        <PlayButton
          playing={playing}
          loading={sample.isFetching}
          disabled={!canPlay}
          onClick={handlePlay}
          title={
            !canPlay
              ? 'Нечего слушать: записи нет'
              : sample.error
                ? 'Образец недоступен'
                : playing
                  ? 'Остановить'
                  : 'Прослушать образец'
          }
          ariaLabel={playing ? 'Остановить образец' : `Прослушать образец: ${row.name}`}
        />
        {playing && (
          <Scrubber
            positionMs={audio.positionMs}
            durationMs={audio.durationMs}
            onSeek={audio.seek}
            ariaLabel={`Перемотка образца: ${row.name}`}
          />
        )}
        </div>
      </td>

      <td>
        <span className={styles.name}>
          <span
            className={styles.nameText}
            title={row.name}
          >
            {row.name}
          </span>
          {voice?.is_current && (
            <span className={styles.star} title="Основной голос персонажа">
              <Star size={12} strokeWidth={1.6} fill="currentColor" />
            </span>
          )}
        </span>
      </td>

      <td>
        <Pill variant={row.group === 'mine' ? 'solid' : 'soft'}>{GROUP_LABELS[row.group]}</Pill>
      </td>

      <td className={tableCell.mono} title={language}>
        {language}
      </td>

      <td>
        <span className={styles.cell} title={traitLine([row.timbre, row.description]) || undefined}>
          {row.timbre}
        </span>
      </td>

      <td>
        <span
          className={styles.cell}
          title={row.characterName ?? 'голос аккаунта, ни на кого не привязан'}
        >
          {row.characterName ?? 'нет'}
        </span>
      </td>

      <td>
        <span
          className={styles.cell}
          title={voice?.team ? `Команда: ${teamLabel(voice.team)}` : 'Команда не назначена'}
        >
          {voice?.author_login ?? '—'}
        </span>
      </td>

      <td>
        <span title={row.state.hint ?? undefined}>
          <Pill tone={row.state.tone}>{row.state.label}</Pill>
        </span>
      </td>

      <td className={tableCell.actions}>
        {voice ? (
          <ProfileActions
            voice={voice}
            broken={row.state.broken}
            canEdit={canEdit}
            cloning={cloning}
            cloneBlocked={cloneBlocked}
            onClone={onClone}
          />
        ) : null}
      </td>
    </tr>
  );
}

function ProfileActions({
  voice,
  broken,
  canEdit,
  cloning,
  cloneBlocked,
  onClone,
}: {
  voice: Voice;
  broken: boolean;
  canEdit: boolean;
  cloning: boolean;
  cloneBlocked: boolean;
  onClone: (voiceId: Id) => void;
}) {
  const update = useUpdateVoice(voice.id);
  const { canChange } = useCurrentUser();
  const allowed = canEdit && canChange(voice);

  return (
    <TableActions>
      <TeamSelect
        value={voice.team}
        disabled={update.isPending}
        onChange={(team) => update.mutate({ team })}
      />
      {allowed && !voice.is_current && voice.usable && (
        <Button
          size="xs"
          onClick={() => update.mutate({ is_current: true })}
          disabled={update.isPending}
          title="Сделать основным голосом персонажа"
        >
          {update.isPending ? '…' : 'Сделать основным'}
        </Button>
      )}
      {allowed && (broken || !voice.external_voice_id) && (
        <Button
          size="xs"
          onClick={() => onClone(voice.id)}
          disabled={cloneBlocked}
          title="Создать голос из сохранённой записи"
        >
          <RefreshCw size={11} strokeWidth={1.6} />
          {cloning ? 'клонируем…' : voice.external_voice_id ? 'Переклонировать' : 'Клонировать'}
        </Button>
      )}
      {update.error && <span className={styles.failNote}>не сохранилось</span>}
    </TableActions>
  );
}
